import * as React from 'react';
import { ApolloError } from '@apollo/client';
import { DxfImageInput, DxfInput } from '../generated/graphql';
import { ApiClient } from './ApiClient';
import { UploadFormProgress, UploadPurpose } from './FileUpload.logic';
import { FileUploadError } from './ResumableUpload';

const FORM_UPLOAD_PURPOSES: { [form in UploadForm]: { drawing: UploadPurpose; image: UploadPurpose } } = {
  face: { drawing: 'face-drawing', image: 'face-image' },
  frame: { drawing: 'frame-drawing', image: 'frame-image' },
};

type UploadForm = 'face' | 'frame';

export interface UploadedFormFiles {
  dxf: DxfInput | undefined;
  dxfImages: DxfImageInput[];
}

// 添付済みのファイルを順に送信してから保存する。失敗しても入力と添付は残し、再実行できる
export function useFormFileUpload(form: UploadForm, notify: (message: string | null) => void, showMutationError: (error: ApolloError | null) => void): {
  uploading: boolean;
  uploadProgress: UploadFormProgress | null;
  registering: boolean;
  submitFiles: (drawing: File | null, images: File[], save: (uploaded: UploadedFormFiles) => Promise<void>, onSaved: () => void) => Promise<void>;
} {
  // 画面を開いている間は同じ ApiClient を使い続け、登録だけ失敗したときに送信済みのファイルを送り直さない
  const [apiClient] = React.useState(() => new ApiClient());
  const pageOpenRef = React.useRef(true);
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<UploadFormProgress | null>(null);
  const [registering, setRegistering] = React.useState(false);

  // 画面を閉じたら送信を止め、閉じた後に登録や表示の更新を始めないようにする
  React.useEffect(() => {
    return () => {
      pageOpenRef.current = false;
      apiClient.disposeDirectUploads();
    };
  }, [apiClient]);

  const submitFiles = async (drawing: File | null, images: File[], save: (uploaded: UploadedFormFiles) => Promise<void>, onSaved: () => void) => {
    if (uploading) {
      return;
    }

    setUploading(true);
    notify(null);
    showMutationError(null);
    try {
      const uploaded = await uploadFormFiles(apiClient, form, drawing, images, setUploadProgress);
      if (pageOpenRef.current) {
        setRegistering(true);
        await save(uploaded);
        if (pageOpenRef.current) {
          onSaved();
        }
      }
    } catch (error) {
      if (pageOpenRef.current) {
        showSubmitError(error, notify, showMutationError);
      }
    } finally {
      if (pageOpenRef.current) {
        setUploading(false);
        setRegistering(false);
        setUploadProgress(null);
      }
    }
  };

  return { uploading, uploadProgress, registering, submitFiles };
}

// 図面、関連画像の順に1件ずつ送り、登録に使う入力を作る。どのファイルで失敗したかを特定しやすくするため同時には送らない
async function uploadFormFiles(
  apiClient: ApiClient,
  form: UploadForm,
  drawing: File | null,
  images: File[],
  onProgress: (progress: UploadFormProgress) => void
): Promise<UploadedFormFiles> {
  const purposes = FORM_UPLOAD_PURPOSES[form];
  // 図面は添付したときだけ1件目として数え、関連画像はその次から数える
  let drawingCount = 0;
  if (drawing !== null) {
    drawingCount = 1;
  }
  const fileCount = drawingCount + images.length;
  // 図面が無ければ undefined のままにし、保存時に既存の図面が維持される
  let dxf: DxfInput | undefined = undefined;
  if (drawing !== null) {
    const [fileUrl, fileSize] = await apiClient.uploadDirect(drawing, purposes.drawing, (progress) =>
      onProgress({ ...progress, fileIndex: 1, fileCount })
    );
    dxf = { file_name: drawing.name, file_url: fileUrl, file_size: fileSize };
  }
  const dxfImages: DxfImageInput[] = [];
  for (let i = 0; i < images.length; i++) {
    const fileIndex = drawingCount + i + 1;
    const [fileUrl] = await apiClient.uploadDirect(images[i], purposes.image, (progress) =>
      onProgress({ ...progress, fileIndex, fileCount })
    );
    dxfImages.push({ file_name: images[i].name, file_url: fileUrl });
  }
  return { dxf, dxfImages };
}

function showSubmitError(
  error: unknown,
  notify: (message: string) => void,
  showMutationError: (mutationError: ApolloError) => void
): void {
  if (error instanceof FileUploadError) {
    notify(buildUploadFailureMessage(error.fileName));
  } else if (error instanceof ApolloError) {
    showMutationError(error);
  } else if (error instanceof Error) {
    notify(`保存に失敗しました: ${error.message}`);
  } else {
    notify(buildUploadFailureMessage(null));
  }
}

function buildUploadFailureMessage(failedFileName: string | null): string {
  if (failedFileName !== null) {
    return `アップロードに失敗しました: ${failedFileName}`;
  }
  return '保存に失敗しました。時間をおいて再実行してください。';
}
